import i18n from '@i18next/i18n';
import { EditEvents, IEditEvents } from '../../../core/interfaces/event.interface';
import { ILogger } from '../../../core/interfaces/logger.interface';
import CommonTips, { CommonTipsActionType, CommonTipsType } from '@src/framework/frame/components/common-tips';
import { ContentStatusEvents, IContentStatusManager } from '../../../core/factory/content-status-manager-factory';
import { TDWOperationType, TDWModuleType, TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { Reporter007 } from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import { SlideDataServices } from '../../../core/slide-data-services.core';
import { network } from '@tencent/netcheck';
import { OnlineEditServices } from '../core/online-edit-services.core';
import { IEventManager } from '@tencent/slide-sdk-model';
import type { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { shouldDisableSocket } from '@src/slide/infrastructure/module/dependent/socket';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';

const MODULE = '[OnlineEditNoticeManager]';

export class OnlineEditNoticeManager {
    private getAppServiceProviderPromise: Promise<IEditorAppServices>;

    public constructor(
        private logger: ILogger,
        private sdkEmitter: IEventManager,
        private emitter: IEditEvents,
        private contentStatusManager: IContentStatusManager,
    ) {
        this.getAppServiceProviderPromise = new Promise<IEditorAppServices>((resolve) => {
            const appServiceProviderLoadedOff = this.emitter.on(
                EditEvents.AppServiceProviderLoaded,
                (appService: IEditorAppServices) => {
                    appServiceProviderLoadedOff();
                    resolve(appService);
                },
            );
        });

        const loadDocumentErrorOff = this.emitter.on(EditEvents.LoadDocumentError, (e?: any) => {
            loadDocumentErrorOff();
            CommonTips.getInstance().hide();
            CommonTips.getInstance().show({
                message: e?.details?.message || i18n.t('文档打开失败，请刷新重试'),
                commonTipsType: CommonTipsType.INFO,
                onlyShowOne: true,
                actionType: e?.details?.actionType || CommonTipsActionType.HOWTODEAL,
            });
            this.logger.monitor?.reportStatus?.('fetchChunkData', Reporter007.ERROR, e?.toString(), [this.getGlobalPadId(), `${network.isOnline}`]);
            this.logger.error(`${MODULE} LoadDocumentError, ${e?.toString()}`);
        });

        const loadDocumentOff = this.emitter.on(EditEvents.LoadDocumentAllChunksSuccess, (loadLog) => {
            loadDocumentOff();
            this.logger.tdw?.({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PPT,
                action: TDWActionType.LOAD_DOC,
                ver6: JSON.stringify(loadLog),
            });
        });

        const LoadErrorOff = this.contentStatusManager.emitter.on(ContentStatusEvents.LoadError, (e) => {
            LoadErrorOff();
            this.logger.tdw?.({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PPT,
                action: TDWActionType.BROKEN_DOC,
            });
            this.logger.monitor?.reportStatus?.(
                TDWActionType.BROKEN_DOC,
                Reporter007.ERROR,
                e?.toString(),
                [this.getGlobalPadId()],
            );
            CommonTips.getInstance().hide();
            CommonTips.getInstance().show({
                message: i18n.t('文档数据异常，请刷新重试'),
                commonTipsType: CommonTipsType.ERROR,
                onlyShowOne: true,
                actionType: CommonTipsActionType.HOWTODEAL,
                callBack: () => {
                    this.logger.tdw?.({
                        opername: TDWOperationType.DOC_PPT,
                        module: TDWModuleType.PPT,
                        action: TDWActionType.EXPORT_BROKEN_DOC,
                    });
                },
            });
            this.logger.error(`${MODULE} LoadError, ${e?.toString()}`, [this.getGlobalPadId(), `${network.isOnline}`]);
        });

        OnlineEditServices.OTTransformManager.promise().then((otTransformManager) => {
            if (!otTransformManager.supported) {
                CommonTips.getInstance().show({
                    message: i18n.t('当前环境只支持查看，请下载最新版客户端或Chrome浏览器'),
                    commonTipsType: CommonTipsType.INFO,
                    onlyShowOne: true,
                    closable: true,
                });
            }
        });

        // FIXME: meeting-room 不应该引入任何品类代码。
        // 1. 移除 /src/page/toc/meeting-room/app-starter.ts 中的 entry 引用
        // 2. 删除这个全局变量
        if ((window as any).entry === 'meeting-room' || shouldDisableSocket()) return;
        this.preventEditWhenNotReady();
    }

    /**
     * 在 SDK CollaborationReady 完成之前，要阻止用户编辑行为
     */
    private async preventEditWhenNotReady() {
        // 文库来源的定制化需求
        if (ENV.IS_WENKU) {
            CommonTips.getInstance().show({
                message: i18n.t('已保存至腾讯文档，可下载到本地保存'),
                commonTipsType: CommonTipsType.INFO,
                closable: false,
                block: false,
                actionType: CommonTipsActionType.EXPORT,
                actionText: i18n.t('下载'),
                autoClose: true,
                duration: 3000,
            });
            return;
        }
        await SlideDataServices.DocInfoManager.promise();

        // SDK Persist 加载比较晚，所以这里自己取标志位。临时解决方案，比较戳的实现，勿喷，可联系 @rongodzhang
        // 需 src/slide/application/service/register-manager/core/listeners/persistent-listener.ts 联动配合
        if (this.hasOfflineData()) {
            CommonTips.getInstance().show({
                message: i18n.t('正在恢复数据，请耐心等待，整个过程可能会持续几分钟...'),
                commonTipsType: CommonTipsType.INFO,
                block: true,
                closable: false,
                id: 'recovery-tips-id',
            });
        }
    }

    private getGlobalPadId() {
        return SlideDataServices.DocInfoManager()?.globalPadId ?? '';
    }

    private getUid() {
        return SlideDataServices.DocInfoManager()?.userId ?? '';
    }

    private hasOfflineData(): boolean {
        return localStorage.getItem(`${this.getGlobalPadId()}-${this.getUid()}-has-offline-data`) === 'true';
    }
}
